/**
 * Screens 15 «Расписание» and 15a «Правка дня» (salon-mockup.html schedule /
 * hoursedit; D-29, D-61). The switch in the list turns a day on or off at
 * once; a row opens 15a. Every save is one PUT /schedule of the whole week;
 * a weekday left out is a day off. No «Обед» row until the schema has a
 * recurring break (D-56).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { AppBar, Sheet, Shell } from '../components/Shell';
import { Wheel } from '../components/Wheel';
import { Icon } from '../components/icons';
import { ApiError, api } from '../core/api';
import { errorText } from '../i18n';
import type { DayHours } from '../types';

// A day switched on from the list gets registration's hours (D-38).
const DEFAULT_HOURS = { opens_at: '09:00', closes_at: '18:00' };
const short = (t: string) => t.slice(0, 5);

export function useSaveWeek() {
  const qc = useQueryClient();
  return async (week: DayHours[]) => {
    const saved = await api.putSchedule(week.map((d) => ({ weekday: d.weekday, opens_at: short(d.opens_at), closes_at: short(d.closes_at) })));
    qc.setQueryData(['schedule'], saved);
    // Hours shape the free gaps on every calendar view (D-37).
    for (const key of ['today', 'day', 'week', 'month']) void qc.invalidateQueries({ queryKey: [key] });
  };
}

export function ScheduleScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { weekday } = useParams();
  const schedule = useQuery({ queryKey: ['schedule'], queryFn: api.schedule });
  const saveWeek = useSaveWeek();
  const [error, setError] = useState<string | null>(null);
  const names = t('schedule.days', { returnObjects: true }) as string[];
  const week = schedule.data ?? [];
  const byDay = (d: number) => week.find((h) => h.weekday === d);

  const toggle = async (d: number) => {
    const next = byDay(d) ? week.filter((h) => h.weekday !== d) : [...week, { weekday: d, ...DEFAULT_HOURS }];
    setError(null);
    try {
      await saveWeek(next);
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    }
  };

  return (
    <Shell active="settings">
      <AppBar
        title={t('schedule.title')}
        right={
          <button type="button" className="iconbtn" aria-label={t('app.back')} onClick={() => navigate('/settings')}>
            <Icon.back />
          </button>
        }
      />
      <div className="content">
        <div className="pane">
          {error && <div className="warnbox form-error" role="alert">{error}</div>}
          <span className="cap">{t('schedule.week')}</span>
          <div className="seclist">
            {schedule.data &&
              names.map((name, d) => {
                const h = byDay(d);
                return (
                  <div key={d} className="si" data-weekday={d} role="button" tabIndex={0} onClick={() => navigate(`/settings/schedule/${d}`)}>
                    <span className="grow">
                      <b style={{ fontSize: 14 }}>{name}</b>
                      <span className="sm tnum" style={{ display: 'block' }}>
                        {h ? `${short(h.opens_at)} — ${short(h.closes_at)}` : t('schedule.off')}
                      </span>
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!h}
                      aria-label={name}
                      className={`sw-t${h ? '' : ' off'}`}
                      style={{ border: 0, padding: 0, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggle(d);
                      }}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      {weekday !== undefined && schedule.data && Number(weekday) >= 0 && Number(weekday) <= 6 && (
        <DaySheet key={weekday} day={Number(weekday)} name={names[Number(weekday)]} week={week} onClose={() => navigate('/settings/schedule')} />
      )}
    </Shell>
  );
}

function DaySheet({ day, name, week, onClose }: { day: number; name: string; week: DayHours[]; onClose: () => void }) {
  const { t } = useTranslation();
  const saveWeek = useSaveWeek();
  const current = week.find((h) => h.weekday === day);
  const [on, setOn] = useState(!!current);
  const [opens, setOpens] = useState(short(current?.opens_at ?? DEFAULT_HOURS.opens_at));
  const [closes, setCloses] = useState(short(current?.closes_at ?? DEFAULT_HOURS.closes_at));
  const [editing, setEditing] = useState<'opens' | 'closes'>('closes');
  const [allWeekdays, setAllWeekdays] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (on && closes <= opens) return setError(t('schedule.order'));
    // «Применить ко всем будням» copies this day to Monday-Friday in the same save (D-29).
    const days = allWeekdays ? [0, 1, 2, 3, 4, day] : [day];
    const next = week.filter((h) => !days.includes(h.weekday));
    if (on) for (const d of new Set(days)) next.push({ weekday: d, opens_at: opens, closes_at: closes });
    setBusy(true);
    try {
      await saveWeek(next);
      onClose();
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  };

  const field = (which: 'opens' | 'closes', label: string, value: string) => (
    <div className="fld grow">
      <label>{label}</label>
      <div className={`inp tnum${editing === which ? ' focus' : ''}`} role="button" tabIndex={0} aria-label={label} onClick={() => setEditing(which)}>
        {value}
      </div>
    </div>
  );

  return (
    <Sheet onClose={onClose} className="narrow">
      <form onSubmit={submit} noValidate>
        <h3>{name}</h3>
        {error && <div className="warnbox form-error" role="alert">{error}</div>}
        <div className="seclist">
          <div className="si">
            <span className="grow">
              <b style={{ fontSize: 14 }}>{t('schedule.workday')}</b>
            </span>
            <button type="button" role="switch" aria-checked={on} aria-label={t('schedule.workday')} className={`sw-t${on ? '' : ' off'}`} style={{ border: 0, padding: 0, cursor: 'pointer' }} onClick={() => setOn(!on)} />
          </div>
        </div>
        {on && (
          <>
            <div className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
              {field('opens', t('schedule.opens'), opens)}
              {field('closes', t('schedule.closes'), closes)}
            </div>
            <Wheel
              label={editing === 'opens' ? t('schedule.opens') : t('schedule.closes')}
              value={editing === 'opens' ? opens : closes}
              onChange={(hm) => (editing === 'opens' ? setOpens(hm) : setCloses(hm))}
            />
          </>
        )}
        <label className="chk" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={allWeekdays} onChange={(e) => setAllWeekdays(e.target.checked)} style={{ position: 'absolute', opacity: 0 }} />
          <span className="chk-box" style={allWeekdays ? { background: 'var(--accent)', borderColor: 'var(--accent)' } : undefined} />
          <span>{t('schedule.allWeekdays')}</span>
        </label>
        <div className="btnrow">
          <button type="button" className="btn" onClick={onClose}>
            {t('services.cancel')}
          </button>
          <button type="submit" className="btn pri" disabled={busy}>
            {t('services.save')}
          </button>
        </div>
      </form>
    </Sheet>
  );
}
